import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ChevronDown, Share2 } from "lucide-react";
import QRCode from "qrcode.react";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";
import { useShareGenieLink } from "@/hooks/voice/useShareGenieLink";
import { inkOn, tint } from "@/pages/help-hub/brandInk";
import type { GenieDetailsPanelProps } from "@/types/voice/genie-details";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { POOF_MOTION } from "./geniePanelShared";
import { ResourceLoungeContent } from "./ResourceLoungeContent";
import { CustomerMemoryControl } from "./CustomerMemoryControl";
import { useLiveAgentOptional } from "../LiveAgentContext";

/** A compact resource list. Optional take-away controls expand on request. */
export const GenieDetailsPanel = ({
  open,
  onClose,
  brandColor: brandColorProp,
  secondaryColor: secondaryColorProp,
  agentName: agentNameProp,
  logoUrl: logoUrlProp,
  resources,
  qrLogoUrl: qrLogoUrlProp,
  agentId: agentIdProp,
  agentUserId: agentUserIdProp,
  helpPageUrl,
  onSummaryCaptured: onSummaryCapturedProp,
  memoryConsent,
  memoryUpdating,
  onMemoryConsentChange,
  helpHub,
  surfacedResourceId,
  shareUrl,
}: GenieDetailsPanelProps) => {
  const liveAgent = useLiveAgentOptional();
  const brandColor = brandColorProp ?? liveAgent?.brandColor ?? DEFAULT_BRANDING.primaryColor;
  const secondaryColor = secondaryColorProp ?? liveAgent?.secondaryColor ?? brandColor;
  const agentName = agentNameProp ?? liveAgent?.agentName;
  const logoUrl = logoUrlProp ?? liveAgent?.logoUrl;
  const qrLogoUrl = qrLogoUrlProp ?? liveAgent?.qrLogoUrl;
  const agentId = agentIdProp ?? liveAgent?.agentId;
  const agentUserId = agentUserIdProp ?? liveAgent?.agentUserId;
  const onSummaryCaptured = onSummaryCapturedProp ?? liveAgent?.onContactCaptured;
  const [pageUrl, setPageUrl] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const reduceMotion = useReducedMotion();
  const sharing = useShareGenieLink(shareUrl || pageUrl, agentName || "Your Genie");
  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "details_panel",
    onCaptured: onSummaryCaptured,
  });

  useEffect(() => {
    setPageUrl(`${window.location.origin}${window.location.pathname}`);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
      if (event.key !== "Tab") return;
      const controls = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex="0"]',
      ) ?? []).filter(element => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previousFocus?.focus();
    };
  }, [open]);

  const disclosureClass = "group border-b border-genie-border last:border-b-0";
  const summaryClass = "flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-4 py-3 text-sm text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary [&::-webkit-details-marker]:hidden";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="genie-details"
          {...(reduceMotion
            ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
            : POOF_MOTION)}
          ref={panelRef}
          tabIndex={-1}
          className="absolute inset-0 z-[1500] overflow-y-auto overflow-x-hidden bg-genie-background outline-none"
          role="dialog"
          aria-modal="true"
          aria-label={`Links and resources for ${agentName || "this Genie"}`}
        >
          <div className="mx-auto w-full max-w-3xl px-5 py-6 sm:px-8 sm:py-12">
            <button type="button" onClick={onClose} className="mb-8 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to Genie
            </button>

            <header className="mb-7 flex items-center gap-4">
              {logoUrl && (
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl" style={{ background: tint(brandColor, 0.08) }}>
                  <img src={logoUrl} alt="" className="h-10 w-10 object-contain" />
                </span>
              )}
              <div className="min-w-0">
                <p className="mb-1 break-words text-sm text-gray-500">{agentName || "Your Genie"}</p>
                <h2 className="text-3xl font-bold tracking-tight text-[#222B45]">Links & resources</h2>
              </div>
            </header>

            <ResourceLoungeContent
              resources={resources}
              helpHub={helpHub}
              helpPageUrl={helpPageUrl}
              surfacedResourceId={surfacedResourceId}
              brandColor={brandColor}
              secondaryColor={secondaryColor}
            />

            <div className="mt-6" aria-label="Optional extras">
              {agentId && (
                <details className={disclosureClass} onToggle={event => { if (event.currentTarget.open) capture.markShown(); }}>
                  <summary className={summaryClass}>
                    Email me a summary
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <div className="max-w-md pb-5 pt-2 [&_button[type=submit]]:text-inherit [&_button[type=button]]:min-h-[44px]" style={{ color: inkOn(brandColor) }}>
                    <SummaryCaptureForm
                      brandColor={brandColor}
                      secondaryColor={brandColor}
                      values={capture.values}
                      onChange={capture.onChange}
                      expanded={false}
                      onToggleExpanded={capture.toggleExpanded}
                      onSubmit={capture.submit}
                      isSubmitting={capture.isSubmitting}
                      submitted={capture.submitted}
                      error={capture.error}
                      hideHeader
                      hideExpandToggle
                      alreadyCapturedEmail={capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null}
                      onChangeEmail={capture.startEdit}
                    />
                  </div>
                </details>
              )}
              {(shareUrl || pageUrl) && (
                <details className={disclosureClass}>
                  <summary className={summaryClass}>
                    Share this Genie
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <div className="flex items-center gap-5 pb-5 pt-2">
                    <div className="hidden shrink-0 rounded-xl border border-genie-border bg-white p-2 sm:block">
                      <QRCode value={shareUrl || pageUrl} size={104} level="H" includeMargin imageSettings={qrLogoUrl ? { src: qrLogoUrl, height: 24, width: 24, excavate: true } : undefined} />
                    </div>
                    <div className="min-w-0">
                      <p className="mb-2 text-sm text-gray-500">Save the link or send it to someone.</p>
                      <button type="button" onClick={() => void sharing.share()} disabled={sharing.sharing} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-genie-border bg-white px-4 text-sm font-medium text-gray-700 disabled:opacity-50">
                        <Share2 className="h-4 w-4" /> Share link
                      </button>
                      <p className="mt-3 select-all break-all text-xs text-gray-500">{shareUrl || pageUrl}</p>
                    </div>
                  </div>
                </details>
              )}
              {memoryConsent && onMemoryConsentChange && (
                <details className={disclosureClass}>
                  <summary className={summaryClass}>
                    Conversation memory
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <div className="pb-5 pt-2">
                    <CustomerMemoryControl status={memoryConsent} isUpdating={memoryUpdating} onChange={onMemoryConsentChange} compact />
                  </div>
                </details>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenieDetailsPanel;
